import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { SnackbarProvider } from 'notistack'
import { Provider } from 'react-redux'
import { store } from './store'
import theme from './theme'
import Layout from './components/LayoutMUI'
import Login from './pages/LoginMUI'
import Products from './pages/ProductsMUI'
import ProductAddEdit from './pages/ProductAddEdit'
import BulkOperations from './pages/BulkOperations'
import CatalogueManagementTabs from './pages/CatalogueManagementTabs'
import InventoryManagementTabs from './pages/InventoryManagementTabs'
import Customers from './pages/CustomersMUI'
import CustomerFormPage from './pages/CustomerFormSimple'
import CustomerGroupFormPage from './pages/CustomerGroupFormPage'
import CustomerGroupsPage from './pages/CustomerGroupsPage'
import CustomerLedgerPage from './pages/CustomerLedgerPage'
import CustomerDetailPage from './pages/CustomerDetailPage'
import SupplierLedgerPage from './pages/SupplierLedgerPage'
import PartyLedgersPage from './pages/PartyLedgersPage'
import PartyManagementPage from './pages/PartyManagementPage'
import PricingTierFormPage from './pages/PricingTierFormPage'
import BulkPricingPage from './pages/BulkPricingPage'
import Inventory from './pages/InventoryMUI'
import InventoryBalancing from './pages/InventoryBalancingMUI'
import InventoryReports from './pages/InventoryReportsMUI'
import StockAdjustmentsEnhanced from './pages/StockAdjustmentsEnhanced'
import BulkStockAdjustmentPage from './pages/BulkStockAdjustmentPage'
import StockMovementHistory from './pages/StockMovementHistory'
import ReportGenerator from './pages/ReportGeneratorMUI'
import AuditLogViewer from './pages/AuditLogViewerMUI'
import PurchaseOrderManagementMUI from './pages/PurchaseOrderManagementMUI'
import SupplierManagementMUI from './pages/SupplierManagementMUI'
import SupplierFormSimple from './pages/SupplierFormSimple'
import EstimatesList from './pages/documents/estimates/EstimatesList'
import EstimateForm from './pages/documents/estimates/EstimateForm'
import EstimateDetail from './pages/documents/estimates/EstimateDetail'
import SalesOrdersPage from './pages/documents/SalesOrdersPage'
import SalesOrderForm from './pages/documents/sales-orders/SalesOrderForm'
import SalesOrderDetail from './pages/documents/sales-orders/SalesOrderDetail'
import SalesReturnsPage from './pages/documents/SalesReturnsPage'
import SalesReturnForm from './pages/documents/sales-returns/SalesReturnForm'
import SalesReturnDetail from './pages/documents/sales-returns/SalesReturnDetail'
import DeliveryChallansPage from './pages/documents/DeliveryChallansPage'
import DeliveryChallanForm from './pages/documents/delivery-challans/DeliveryChallanForm'
import { FeatureUnavailable } from './components/common/FeatureUnavailable'
import DeliveryChallanDetail from './pages/documents/delivery-challans/DeliveryChallanDetail'
import { PurchaseBillsList, PurchaseBillDetail, PurchaseBillForm } from './pages/documents/purchase-bills'
import { PurchaseReturnsList } from './pages/documents/purchase-returns'
import { PurchaseDashboard } from './pages/PurchaseDashboard'
import InvoicesPage from './pages/documents/InvoicesPage'
import InvoiceDetail from './pages/documents/invoices/InvoiceDetail'
import InvoiceForm from './pages/documents/invoices/InvoiceForm'
import PurchaseOrderForm from './pages/documents/purchase-orders/PurchaseOrderForm'
import PurchaseOrderDetail from './pages/documents/purchase-orders/PurchaseOrderDetail'
import AuditExportPage from './pages/AuditExportPage'
import EmailConfigurationPage from './pages/EmailConfigurationPage'
import SMSConfigurationPage from './pages/SMSConfigurationPage'
import WhatsAppConfigurationPage from './pages/WhatsAppConfigurationPage'
import ShippingConfigurationPage from './pages/ShippingConfigurationPage'
import IntegrationHealthMonitor from './pages/IntegrationHealthMonitor'
import IntegrationConfigPage from './pages/IntegrationConfigPage'
import IntegrationFormPage from './pages/IntegrationFormPage'
import IntegrationHealthPage from './pages/IntegrationHealthPage'
import IntegrationAnalyticsPage from './pages/IntegrationAnalyticsPage'
import IntegrationTemplatesPage from './pages/IntegrationTemplatesPage'
import TemplateEditorPage from './pages/TemplateEditorPage'
import TemplatePreviewPage from './pages/TemplatePreviewPage'
import WebhookManagement from './pages/WebhookManagement'
import InvoiceTemplateConfigurationPage from './pages/InvoiceTemplateConfigurationPage'
import DocumentSequenceConfigurationPage from './pages/DocumentSequenceConfigurationPage'
import PaymentRecordPage from './pages/PaymentRecordPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminUserFormPage from './pages/AdminUserFormPage'
import RolesPage from './pages/RolesPage'
import RazorpayConfigurationPage from './pages/RazorpayConfigurationPage'
import SalesDashboard from './pages/SalesDashboard'
import MainDashboard from './pages/MainDashboard'
import CustomerAnalyticsDashboard from './pages/CustomerAnalyticsDashboard'
import CustomerBehaviorDashboard from './pages/CustomerBehaviorDashboard'
import ActiveCartsPage from './pages/ActiveCartsPage'
import WarehouseManagementPage from './pages/WarehouseManagementPage'
import LeadsPage from './pages/LeadsPage'
import LeadDetailPage from './pages/LeadDetailPage'
import LeadFormPage from './pages/LeadFormPage'

import { useSelector } from 'react-redux'
import type { RootState } from './store'

function AppRoutes() {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/*" element={
        isAuthenticated ? (
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

              {/* Main Dashboard */}
              <Route path="/admin/dashboard" element={<MainDashboard />} />

              {/* Sales Dashboard */}
              <Route path="/admin/sales/dashboard" element={<SalesDashboard />} />

              {/* Sales Documents */}
              <Route path="/admin/documents/estimates" element={<EstimatesList />} />
              <Route path="/admin/documents/estimates/create" element={<EstimateForm />} />
              <Route path="/admin/documents/estimates/:id" element={<EstimateDetail />} />
              <Route path="/admin/documents/estimates/:id/edit" element={<EstimateForm />} />

              <Route path="/admin/documents/sales-orders" element={<SalesOrdersPage />} />
              <Route path="/admin/documents/sales-orders/create" element={<SalesOrderForm />} />
              <Route path="/admin/documents/sales-orders/:id" element={<SalesOrderDetail />} />
              <Route path="/admin/documents/sales-orders/:id/edit" element={<SalesOrderForm />} />

              <Route path="/admin/documents/invoices" element={<InvoicesPage />} />
              <Route path="/admin/documents/invoices/create" element={<InvoiceForm />} />
              <Route path="/admin/documents/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/admin/documents/invoices/:id/edit" element={<InvoiceForm />} />

              <Route path="/admin/documents/delivery-challans" element={<DeliveryChallansPage />} />
              <Route path="/admin/documents/delivery-challans/create" element={<DeliveryChallanForm />} />
              <Route path="/admin/documents/delivery-challans/:id" element={<DeliveryChallanDetail />} />
              <Route path="/admin/documents/delivery-challans/:id/edit" element={<DeliveryChallanForm />} />

              <Route path="/admin/documents/sales-returns" element={<SalesReturnsPage />} />
              <Route path="/admin/documents/sales-returns/create" element={<SalesReturnForm />} />
              <Route path="/admin/documents/sales-returns/:id" element={<SalesReturnDetail />} />
              <Route path="/admin/documents/sales-returns/:id/edit" element={<SalesReturnForm />} />

              {/* Purchase Documents */}
              <Route path="/admin/purchase/orders" element={<PurchaseOrderManagementMUI />} />
              <Route path="/admin/purchase/orders/create" element={<PurchaseOrderForm />} />
              <Route path="/admin/purchase/orders/:id" element={<PurchaseOrderDetail />} />
              <Route path="/admin/purchase/orders/:id/edit" element={<PurchaseOrderForm />} />

              <Route path="/admin/purchase/bills" element={<PurchaseBillsList />} />
              <Route path="/admin/purchase/bills/create" element={<PurchaseBillForm />} />
              <Route path="/admin/purchase/bills/:id" element={<PurchaseBillDetail />} />
              <Route path="/admin/purchase/bills/:id/edit" element={<PurchaseBillForm />} />

              <Route path="/admin/purchase/returns" element={<PurchaseReturnsList />} />

              <Route path="/admin/purchase/dashboard" element={<PurchaseDashboard />} />

              {/* Party Management */}
              <Route path="/admin/parties" element={<PartyManagementPage />} />
              <Route path="/admin/parties/customers" element={<Customers />} />
              <Route path="/admin/parties/customers/create" element={<CustomerFormPage />} />
              <Route path="/admin/parties/customers/:id" element={<CustomerDetailPage />} />
              <Route path="/admin/parties/customers/:id/edit" element={<CustomerFormPage />} />
              <Route path="/admin/parties/customers/:customerId/ledger" element={<CustomerLedgerPage />} />

              <Route path="/admin/parties/suppliers" element={<SupplierManagementMUI />} />
              <Route path="/admin/parties/suppliers/create" element={<SupplierFormSimple />} />
              <Route path="/admin/parties/suppliers/:id/edit" element={<SupplierFormSimple />} />
              <Route path="/admin/parties/suppliers/:supplierId/ledger" element={<SupplierLedgerPage />} />

              <Route path="/admin/parties/customer-groups" element={<CustomerGroupsPage />} />
              <Route path="/admin/parties/customer-groups/new" element={<CustomerGroupFormPage />} />
              <Route path="/admin/parties/customer-groups/edit/:id" element={<CustomerGroupFormPage />} />
              <Route path="/admin/parties/ledgers" element={<PartyLedgersPage />} />

              {/* Lead Management */}
              <Route path="/admin/leads" element={<LeadsPage />} />
              <Route path="/admin/leads/create" element={<LeadFormPage />} />
              <Route path="/admin/leads/import" element={<FeatureUnavailable featureName="Bulk Import" />} />
              <Route path="/admin/leads/segments" element={<FeatureUnavailable featureName="Segments" />} />
              <Route path="/admin/leads/:leadId" element={<LeadDetailPage />} />
              <Route path="/admin/leads/:leadId/edit" element={<LeadFormPage />} />

              {/* Products & Inventory */}
              <Route path="/admin/products" element={<Products />} />
              <Route path="/admin/products/create" element={<ProductAddEdit />} />
              <Route path="/admin/products/:id/edit" element={<ProductAddEdit />} />
              <Route path="/admin/products/:id/view" element={<ProductAddEdit />} />
              {/* Bulk Operations temporarily disabled */}
              <Route
                path="/admin/products/bulk"
                element={<FeatureUnavailable featureName="Bulk Operations" />}
              />

              <Route path="/admin/catalogue-management" element={<CatalogueManagementTabs />} />
              <Route path="/admin/inventory-management" element={<InventoryManagementTabs />} />

              <Route path="/admin/inventory" element={<Inventory />} />
              <Route path="/admin/inventory/adjustments" element={<StockAdjustmentsEnhanced />} />
              <Route path="/admin/inventory/bulk-adjust" element={<BulkStockAdjustmentPage />} />
              <Route path="/admin/inventory/movements" element={<StockMovementHistory />} />
              <Route path="/admin/inventory/balancing" element={<InventoryBalancing />} />
              {/* Alerts temporarily disabled */}
              <Route
                path="/admin/inventory/alerts"
                element={<FeatureUnavailable featureName="Inventory Alerts" />}
              />
              <Route path="/admin/inventory/reports" element={<InventoryReports />} />

              {/* Pricing */}
              <Route path="/admin/pricing/tiers/new" element={<PricingTierFormPage />} />
              <Route path="/admin/pricing/tiers/edit/:id" element={<PricingTierFormPage />} />
              <Route path="/admin/pricing/bulk" element={<BulkPricingPage />} />

              {/* Payments */}
              <Route path="/admin/payments/create" element={<PaymentRecordPage />} />

              {/* Analytics & Reports */}
              <Route path="/admin/analytics/sales" element={<SalesDashboard />} />
              <Route path="/admin/analytics/customers" element={<CustomerAnalyticsDashboard />} />
              <Route path="/admin/analytics/behavior" element={<CustomerBehaviorDashboard />} />
              <Route path="/admin/carts/active" element={<ActiveCartsPage />} />
              <Route path="/admin/reports/custom" element={<ReportGenerator />} />
              <Route path="/analytics/audit-logs" element={<AuditLogViewer />} />
              <Route path="/admin/audit/export" element={<AuditExportPage />} />

              {/* Administration */}
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/users/create" element={<AdminUserFormPage />} />
              <Route path="/admin/users/:id/edit" element={<AdminUserFormPage />} />
              <Route path="/admin/roles" element={<RolesPage />} />

              {/* Configuration */}
              <Route path="/admin/configuration/invoice-templates" element={<InvoiceTemplateConfigurationPage />} />
              <Route path="/admin/configuration/document-sequences" element={<DocumentSequenceConfigurationPage />} />
              <Route path="/admin/configuration/warehouses" element={<WarehouseManagementPage />} />

              {/* Integrations */}
              <Route path="/admin/integrations/config" element={<IntegrationConfigPage />} />
              <Route path="/admin/integrations/config/create" element={<IntegrationFormPage />} />
              <Route path="/admin/integrations/config/:id/edit" element={<IntegrationFormPage />} />
              <Route path="/admin/integrations/health" element={<IntegrationHealthPage />} />
              <Route path="/admin/integrations/analytics" element={<IntegrationAnalyticsPage />} />
              <Route path="/admin/integrations/templates" element={<IntegrationTemplatesPage />} />
              <Route path="/admin/integrations/templates/create" element={<TemplateEditorPage />} />
              <Route path="/admin/integrations/templates/:id/edit" element={<TemplateEditorPage />} />
              <Route path="/admin/integrations/templates/:id/preview" element={<TemplatePreviewPage />} />

              {/* Legacy Integration Routes - Redirect to new structure */}
              <Route path="/admin/integrations" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/integrations/razorpay" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/integrations/shipping" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/integrations/email" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/integrations/sms" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/integrations/whatsapp" element={<Navigate to="/admin/integrations/config" replace />} />
              <Route path="/admin/system/health" element={<Navigate to="/admin/integrations/health" replace />} />

              {/* Legacy Routes - Keep minimal for old bookmarks */}
              <Route path="/products" element={<Navigate to="/admin/products" replace />} />
              <Route path="/customers" element={<Navigate to="/admin/parties/customers" replace />} />
              <Route path="/inventory" element={<Navigate to="/admin/inventory" replace />} />
              <Route path="/categories" element={<Navigate to="/admin/catalogue-management" replace />} />
              <Route path="/catalogues" element={<Navigate to="/admin/catalogue-management" replace />} />
              <Route path="/collections" element={<Navigate to="/admin/catalogue-management" replace />} />

              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
    </Routes>
  )
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <AppRoutes />
        </SnackbarProvider>
      </ThemeProvider>
    </Provider>
  )
}

export default App
